import moment from 'moment';

import {
  isCanadianRecord,
  recordFieldValue,
  VerificationReportCheck,
  VerificationReportCheckTone,
} from './entityVerificationReportModel';

/**
 * The answer a verification report gives, in one place: which state the
 * record is in and the figure and sentence that say so. The verification page
 * and the downloaded PDF both read it, so they never describe a record
 * differently.
 */

export type VerificationPageState =
  | 'noplan'
  | 'dboff'
  | 'noinput'
  | 'never'
  | 'running'
  | 'error'
  | 'failed'
  | 'open'
  | 'clean';

export interface VerificationAttempt {
  /** Checks whose lookup did not answer, so they keep their earlier result. */
  staleCheckIds: string[];
  /** True when no lookup answered at all. */
  nothingUpdated: boolean;
}

export type VerificationSummaryTone =
  | VerificationReportCheckTone
  | 'locked'
  | 'running';

export interface VerificationSummary {
  figure: string;
  figureTone: VerificationSummaryTone;
  tail: string;
  summary: string;
}

/** What a Team without business verification is told, wherever it is told. */
export const VERIFICATION_NOT_ON_PLAN =
  'Business verification confirms that a company’s domain, email, phone and address really belong to it. It is not included in your team’s plan; contact sales to add it.';

export const SEVERITY: Record<VerificationReportCheckTone, number> = {
  failed: 0,
  review: 1,
  input: 1,
  'not-run': 2,
  unavailable: 2,
  passed: 3,
};

const SHORT_NAMES: Record<string, string> = {
  'domain-contact': 'the domain',
  'listing-consistency': 'the listing',
  'website-contact': 'the website',
  'business-registration': 'registration',
  'email-verification': 'email',
  'phone-validation': 'phone',
  'address-facility': 'address',
};

export const formatVerificationDate = (value: string | null): string =>
  value ? moment(value).format('MMM D, YYYY') : '';

export const listSentence = (items: string[]): string => {
  if (items.length <= 1) return items.join('');
  return `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}`;
};

const capitalize = (value: string) =>
  value ? value.charAt(0).toUpperCase() + value.slice(1) : value;

export const isAttentionTone = (tone: VerificationReportCheckTone): boolean =>
  tone !== 'passed' && tone !== 'failed';

/** Why a Verification Run cannot start right now, as the run buttons' tooltip. */
export const VERIFICATION_RUN_BLOCKED = {
  noCredits: 'No Business Verification credits left this month.',
  databaseScreening:
    'This database is being screened. Run verification once it finishes.',
} as const;

/**
 * The reason the record band and the verification page give for a run that
 * cannot start, or null when it can. Running out of credits outranks a screen
 * in progress: a screen ends on its own, credits do not. Credits that could not
 * be read never block; the service refuses the run itself if none are left.
 */
export const verificationRunBlockedReason = ({
  credits,
  databaseScreening,
}: {
  credits: { total: number; remaining: number } | null;
  databaseScreening: boolean;
}): string | null => {
  if (credits !== null && credits.remaining <= 0) {
    return VERIFICATION_RUN_BLOCKED.noCredits;
  }
  if (databaseScreening) return VERIFICATION_RUN_BLOCKED.databaseScreening;
  return null;
};

export const hasVerificationResults = (
  checks: VerificationReportCheck[],
): boolean =>
  checks.some((check) => ['passed', 'failed', 'review'].includes(check.tone));

/** The entity's review status, as the record page labels it. */
export const entityStatusLabel = (statusFlag: unknown): string => {
  const flag = String(statusFlag || 'new').toLowerCase();
  return (
    (
      {
        approved: 'Cleared',
        cleared: 'Cleared',
        review: 'Needs review',
        needs_review: 'Needs review',
        not_reviewed: 'Not reviewed',
        quarantined: 'Quarantined',
        barred: 'Barred',
        rejected: 'Blocked',
        new: 'New',
      } as Record<string, string>
    )[flag] || flag.replace(/_/g, ' ')
  );
};

export const resolveVerificationPageState = ({
  supplier,
  checks,
  hasPlan,
  databaseVerificationOn = true,
  running,
  interruptedAttempt,
}: {
  supplier: any;
  checks: VerificationReportCheck[];
  hasPlan: boolean;
  databaseVerificationOn?: boolean;
  running: boolean;
  interruptedAttempt: VerificationAttempt | null;
}): VerificationPageState => {
  if (!hasPlan) return 'noplan';
  if (!databaseVerificationOn) return 'dboff';
  if (running) return 'running';
  if (interruptedAttempt) return 'error';
  const canRun = Boolean(
    recordFieldValue(supplier, 'domain') ||
      recordFieldValue(supplier, 'email') ||
      recordFieldValue(supplier, 'phone') ||
      recordFieldValue(supplier, 'address') ||
      (isCanadianRecord(supplier) &&
        (recordFieldValue(supplier, 'registration') ||
          recordFieldValue(supplier, 'name'))),
  );
  if (!hasVerificationResults(checks)) return canRun ? 'never' : 'noinput';
  if (checks.some((check) => check.tone === 'failed')) return 'failed';
  const runnable = checks.filter((check) => check.tone !== 'unavailable');
  if (runnable.length && runnable.every((check) => check.tone === 'passed')) {
    return 'clean';
  }
  return 'open';
};

export const describeVerificationResult = ({
  state,
  supplier,
  checks,
  databaseName,
  lastRunAt,
  elapsedSeconds = 0,
  interruptedAttempt,
}: {
  state: VerificationPageState;
  supplier: any;
  checks: VerificationReportCheck[];
  databaseName: string;
  lastRunAt: string | null;
  elapsedSeconds?: number;
  interruptedAttempt: VerificationAttempt | null;
}): VerificationSummary => {
  const entityName = supplier?.name || 'this entity';
  const failedChecks = checks.filter((check) => check.tone === 'failed');
  const attentionChecks = checks.filter((check) => isAttentionTone(check.tone));
  const passedChecks = checks.filter((check) => check.tone === 'passed');
  const registryUnavailable = checks.some(
    (check) => check.tone === 'unavailable',
  );
  const domainInputs = checks.filter(
    (check) => check.tone === 'input' && check.fix === 'domain',
  ).length;

  // The registry lookup only covers Canada; say so wherever a sentence would
  // otherwise promise that registration gets checked.
  const registryNote = supplier?.country_of_origin
    ? `The registry check covers Canadian companies only, so it does not run for a company in ${supplier.country_of_origin}.`
    : 'The registry check covers Canadian companies only. Add the country if this company is Canadian.';

  const findingsSentence = () => {
    const parts: string[] = [];
    failedChecks.forEach((check) => parts.push(check.description));
    if (domainInputs) {
      parts.push(
        domainInputs > 1
          ? 'No domain is on file, so the registrar and the published contact details could not be read'
          : 'No domain is on file, so the registrar could not be read',
      );
    }
    attentionChecks
      .filter(
        (check) =>
          check.tone === 'review' ||
          (check.tone === 'input' && check.fix !== 'domain'),
      )
      .forEach((check) => parts.push(check.description));
    if (passedChecks.length) {
      const names = passedChecks.map((check) => SHORT_NAMES[check.id]);
      parts.push(
        `${capitalize(listSentence(names))} ${
          names.length > 1 ? 'all check out' : 'checks out'
        }`,
      );
    }
    // Provider summaries often end in their own full stop; drop it so the
    // joined sentence never reads "..".
    const sentence = parts.length
      ? `${parts.map((part) => part.trim().replace(/[.\s]+$/, '')).join('. ')}.`
      : '';
    return registryUnavailable
      ? `${sentence} ${registryNote}`.trim()
      : sentence;
  };

  if (state === 'noplan') {
    return {
      figure: 'Not on your plan',
      figureTone: 'not-run',
      tail: '7 checks',
      summary: VERIFICATION_NOT_ON_PLAN,
    };
  }
  if (state === 'dboff') {
    return {
      figure: 'Turned off',
      figureTone: 'not-run',
      tail: 'for this database',
      summary: `Business verification is turned off for ${databaseName}. A team admin can turn it on in the database's screening settings; until then, no check runs on its records.`,
    };
  }
  if (state === 'never') {
    return {
      figure: 'Not run',
      figureTone: 'not-run',
      tail: '7 checks',
      summary: `Seven checks confirm that the domain, email, phone and address on this record belong to ${entityName}${
        registryUnavailable ? '' : ', and that the company is registered'
      }. One run covers every check this record has a field for, finishes within about a minute, and saves the result here.${
        registryUnavailable ? ` ${registryNote}` : ''
      }`,
    };
  }
  if (state === 'noinput') {
    return {
      figure: 'Nothing to check',
      figureTone: 'review',
      tail: '0 of 7 checks can run',
      summary: `This record has no domain, email, phone or address, so none of the checks has anything to read. Add at least one, then run verification. ${registryNote}`,
    };
  }
  if (state === 'running') {
    return {
      figure: 'Verifying',
      figureTone: 'running',
      tail: `${elapsedSeconds}s elapsed · finishes within about a minute`,
      summary: `Running every check ${entityName} has a field for. A lookup that does not answer in time is skipped, not retried.${
        hasVerificationResults(checks)
          ? ' The last result stays listed until the new one lands.'
          : ''
      }`,
    };
  }
  if (state === 'error') {
    const staleLabels = checks
      .filter((check) =>
        (interruptedAttempt?.staleCheckIds || []).includes(check.id),
      )
      .map((check) => check.label);
    if (interruptedAttempt?.nothingUpdated || !staleLabels.length) {
      return {
        figure: 'Interrupted',
        figureTone: 'failed',
        tail: 'nothing was updated',
        summary: `The verification service did not respond, so no check on this record was updated${
          lastRunAt
            ? `. What is listed below is from the last complete run, on ${formatVerificationDate(
                lastRunAt,
              )}`
            : ''
        }. Try again in a moment.`,
      };
    }
    return {
      figure: 'Interrupted',
      figureTone: 'failed',
      tail: `${staleLabels.length} of 7 checks not updated`,
      summary: `${listSentence(staleLabels)} did not get a response, so ${
        staleLabels.length > 1 ? 'they keep their' : 'it keeps its'
      } earlier result. The other checks were updated just now.`,
    };
  }
  if (state === 'clean') {
    return {
      figure: `${passedChecks.length} of 7`,
      figureTone: 'passed',
      tail: registryUnavailable
        ? 'checks passed · registry not available'
        : 'checks passed',
      summary: registryUnavailable
        ? `The domain, email, phone and address on this record belong to the company. ${registryNote}`
        : 'The company is registered and active, and the domain, email, phone and address on this record belong to it.',
    };
  }
  if (state === 'failed') {
    const reviewCount = attentionChecks.filter(
      (check) => check.tone !== 'unavailable',
    ).length;
    return {
      figure: `${failedChecks.length} failed`,
      figureTone: 'failed',
      tail: [
        reviewCount ? `${reviewCount} to review` : '',
        `${passedChecks.length} passed`,
      ]
        .filter(Boolean)
        .join(' · '),
      summary: `${findingsSentence()} ${
        failedChecks.length > 1 ? 'These are findings' : 'This is a finding'
      } about the company, not ${
        failedChecks.length > 1 ? 'gaps' : 'a gap'
      } in this record, so running again will not clear ${
        failedChecks.length > 1 ? 'them' : 'it'
      }.`,
    };
  }
  const openCount = attentionChecks.filter(
    (check) => check.tone !== 'unavailable',
  ).length;
  return {
    figure: `${openCount} open`,
    figureTone: 'review',
    tail: [
      'of 7 checks',
      `${passedChecks.length} passed`,
      registryUnavailable ? '1 not available' : '',
    ]
      .filter(Boolean)
      .join(' · '),
    summary: findingsSentence(),
  };
};
